import { type ReactElement, useState } from 'react'
import {
  isDefined,
  isEmpty,
  NotificationTopicSchema,
  type NotificationTopic,
  type NotificationView,
} from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { Alert, Button, Loader, Group, Select, Stack, Title } from '@mantine/core'
import { useNotificationUnreadCount, useNotificationList, useTranslation } from '@front/model'
import { Page } from '@components'
import { NotificationViews } from '../../notifications/NotificationViews'
import { NotificationList } from '../../notifications/NotificationList'
import { MarkAllRead } from '../../notifications/MarkAllRead'

export function NotificationsPage(): ReactElement {
  const { t } = useTranslation()
  const [view, setView] = useState<NotificationView>('inbox')
  const [topic, setTopic] = useState<NotificationTopic>()
  const count = useNotificationUnreadCount()
  const enabled = count.available
  const query = useNotificationList({ view, topic }, { enabled })
  function changeTopic(value: string | null): void {
    const parsed = NotificationTopicSchema.safeParse(value)
    setTopic(parsed.success ? parsed.data : undefined)
  }
  if (!enabled)
    return (
      <Page>
        <Page.Content>
          {count.isLoading ? (
            <Loader aria-label='Loading notifications' />
          ) : (
            <Alert title='Notifications' role='alert'>
              {count.unavailable ? 'Notifications are not available.' : count.error}
              <Button variant='subtle' onClick={count.refetch}>
                Retry
              </Button>
            </Alert>
          )}
        </Page.Content>
      </Page>
    )
  return (
    <Page>
      <Page.Content
        scrollArea
        noPadding
        containerProps={{ scrollbars: 'y', styles: { content: { display: 'block', minWidth: 0 } } }}
      >
        <Stack w='100%' maw={768} mx='auto' p={{ base: 'md', sm: 'xl' }}>
          <Group justify='space-between' wrap='nowrap'>
            <Title order={1} size={20}>
              Notifications
            </Title>
            {view !== 'archived' && (
              <MarkAllRead topic={topic} disabled={query.isLoading || !query.isSuccess || isEmpty(query.data)} />
            )}
          </Group>
          <Group justify='space-between'>
            <NotificationViews view={view} onChange={setView} />
            <Select
              w={{ base: '100%', sm: 200 }}
              aria-label='Notification topic'
              value={topic ?? 'all'}
              onChange={changeTopic}
              allowDeselect={false}
              data={[
                { value: 'all', label: 'All' },
                { value: 'areas', label: t('Areas') },
                { value: 'events', label: t('Appointments') },
                { value: 'meetings', label: t('Meetings') },
                { value: 'recordings', label: t('Recordings') },
              ]}
            />
          </Group>
          {isDefined(count.error) && (
            <Alert role='alert' color='red' title='Could not refresh unread count'>
              {count.error}
              <Button onClick={count.refetch}>Retry</Button>
            </Alert>
          )}
          <NotificationList query={query} />
        </Stack>
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/notifications/')({ component: NotificationsPage })
